import IconButton from 'components/IconButton';
import NoRecordFound from 'components/NoRecordFound/NoRecordFound';
import PaginationList from 'components/PaginationList';
import history from 'history.js';
import { useAtomValue } from 'jotai';
import React, { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Table, Row, Col } from 'reactstrap';

import VulnIgnoreModal from './VulnIgnoreModal';
import { appliedCAIdsAtom } from '../../CADashboard/state';
import { appliedEPIdsAtom } from '../../EPDashboard/state';
import { appliedRowIdsAtom } from '../../SASTDashboard2/state';
import { appliedWAIdsAtom } from '../../WADashboard/state';

type Props = {
  qids: string[];
  names: string[];
  counts: number[];
  sourceType: string;
  showQidColumn: boolean;
  hideIgnore: boolean;
  getGroupingDrillDownUrl: (label: string, sourceType: string) => string;
};

const DEFAULT_PAGE_SIZE = 10;
const OTHERS_QID = 'Others';

const VulnerabilitiesListing = ({
  qids,
  names,
  counts,
  sourceType,
  showQidColumn,
  hideIgnore,
  getGroupingDrillDownUrl,
}: Props) => {
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [sumVulnCount, setsumVulnCount] = useState(0);
  const selectedWAIds = useAtomValue(appliedWAIdsAtom);
  const selectedEPIds = useAtomValue(appliedEPIdsAtom);
  const selectedCAIds = useAtomValue(appliedCAIdsAtom);
  const selectedSASTIds = useAtomValue(appliedRowIdsAtom);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [paginatedData, setPaginatedData] = useState<any>(undefined);
  const [Qid, setQid] = useState(0);
  const [showIgnoreModal, setShowIgnoreModal] = useState(false);

  const getTooltip = () => {
    let tooltip = 'Ignore All';
    if (sourceType.includes('ca_data') && selectedCAIds.length > 0) {
      tooltip = 'Ignore for Selected Cloudagents';
    }
    if (sourceType.includes('endpoint_data') && selectedEPIds.length > 0) {
      tooltip = 'Ignore for Selected Endpoints';
    }
    if (sourceType.includes('was_data') && selectedWAIds.length > 0) {
      tooltip = 'Ignore for Selected Webapps';
    }
    if (sourceType.includes('sast') && selectedSASTIds.length > 0) {
      tooltip = 'Ignore for Selected Repositories';
    }
    return tooltip;
  };
  useEffect(() => {
    setTotalCount(qids.length);
    let sum = 0;
    if (counts?.length > 0) {
      sum = counts
        ?.map((a) => a)
        .reduce(function (a, b) {
          return a + b;
        });
    }
    setsumVulnCount(sum);
    let startIndex = (currentPage - 1) * pageSize;
    setPaginatedData({
      qids: qids?.slice(startIndex, startIndex + pageSize),
      names: names?.slice(startIndex, startIndex + pageSize),
      counts: counts?.slice(startIndex, startIndex + pageSize),
    });
  }, [counts, currentPage, names, pageSize, qids]);

  const headerConfig = [
    { title: 'Qualys ID', sortOrder: 'qid' },
    { title: 'SAST Rule ID', sortOrder: 'sonarqube_rule' },
    { title: 'Name', sortOrder: 'name' },
    { title: 'Count', sortOrder: 'count' },
    { title: 'Percentage', sortOrder: 'count' },
    { title: 'Action', sortOrder: '' },
  ];

  const updatedHeaderConfig = showQidColumn
    ? headerConfig.filter((x) => x.title !== 'SAST Rule ID')
    : headerConfig.filter((x) => x.title !== 'Qualys ID');
  const ignoreButton = (
    <i className="fa fa-ban" aria-hidden="true" style={{ color: 'red' }}></i>
  );
  const TableHeader = updatedHeaderConfig.map(({ title }) => {
    return <th key={title}>{title}</th>;
  });

  function getDrillDownUrl(qid: string) {
    if (qid !== OTHERS_QID) {
      const url = getGroupingDrillDownUrl(qid, sourceType);
      return url;
    }

    return '';
  }

  return (
    <Fragment>
      {totalCount > 0 && (
        <Row>
          <Col>
            <Table className="tablesorter table-striped" responsive>
              <thead className="text-primary">
                <tr>{TableHeader}</tr>
              </thead>
              <tbody>
                {paginatedData?.qids?.map((qid, index) => {
                  const vulnName = paginatedData.names[index];

                  const url = getDrillDownUrl(qid);
                  return (
                    <tr
                      key={index}
                      style={{ cursor: 'pointer' }}
                      className={'dynamic-select-tr'}
                      id={qid}
                    >
                      <td>{url ? <Link to={url}>{qid}</Link> : qid}</td>
                      <td>
                        {url ? <Link to={url}>{vulnName}</Link> : vulnName}
                      </td>
                      <td>{paginatedData.counts[index]}</td>
                      <td>
                        {(
                          (paginatedData.counts[index] / sumVulnCount) *
                          100
                        ).toFixed(1)}{' '}
                        %
                      </td>
                      <td className="text-left">
                        {url && (
                          <IconButton
                            id="vulnLink"
                            iconClass="fa fa-bug"
                            size="sm"
                            tooltip="View Vulnerabilities"
                            onClick={() => {
                              history.push(url);
                            }}
                          />
                        )}

                        {!hideIgnore &&
                          sourceType !== 'sast' &&
                          !sourceType.startsWith('ca_data') &&
                          url &&
                          atob(
                            localStorage.getItem(btoa('approle')) ||
                              btoa('client'),
                          ) === 'admin' && (
                            <IconButton
                              id="ignoreLink"
                              iconElement={ignoreButton}
                              size="sm"
                              tooltip={getTooltip()}
                              onClick={() => {
                                setShowIgnoreModal(true);
                                setQid(qid);
                              }}
                            />
                          )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </Col>
        </Row>
      )}
      {totalCount === 0 && <NoRecordFound />}
      {totalCount > DEFAULT_PAGE_SIZE && (
        <PaginationList
          currentPage={currentPage}
          pageSize={pageSize}
          totalCount={totalCount}
          onPageChange={(value) => {
            setCurrentPage(value);
          }}
          onPageSizeChange={(value) => {
            setPageSize(value);
            setCurrentPage(1);
          }}
        />
      )}
      {showIgnoreModal && (
        <VulnIgnoreModal
          sourceType={sourceType}
          qid={Qid}
          onClose={() => setShowIgnoreModal(false)}
        />
      )}
    </Fragment>
  );
};

export default VulnerabilitiesListing;
